import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

interface DailyRevenueData {
    date: string;
    total_revenue: number;
    cafe_revenue: number;
    b2b_revenue: number;
    net_revenue: number;
    order_count: number;
    avg_order_value: number;
}

interface CostCenterRevenueData {
    cost_center_id: string;
    cost_center_name: string;
    total_revenue: number;
    net_revenue: number;
    order_count: number;
    avg_order_value: number;
}

interface ProductCategoryRevenueData {
    category_id: string | null;
    category_name: string;
    total_revenue: number;
    net_revenue: number;
    total_quantity: number;
    item_count: number;
}

interface MonthlyComparisonData {
    current_month: string;
    previous_month: string;
    current_revenue: number;
    current_orders: number;
    current_avg_order: number;
    previous_revenue: number;
    previous_orders: number;
    previous_avg_order: number;
    revenue_growth: number;
    order_growth: number;
    avg_order_growth: number;
}

interface SalesDashboardData {
    cost_center_id?: string;
    start_date: string;
    end_date: string;
    base_currency: string;
    daily_revenue: DailyRevenueData[];
    cost_center_revenue: CostCenterRevenueData[];
    category_revenue: ProductCategoryRevenueData[];
    total_revenue: number;
    total_orders: number;
    avg_order_value: number;
    avg_daily_revenue: number;
}

interface MonthlyComparisonResponse {
    cost_center_id?: string;
    base_currency: string;
    comparison_data: MonthlyComparisonData;
    current_month_daily: DailyRevenueData[];
    previous_month_daily: DailyRevenueData[];
}

interface CostCenter {
    id: string;
    name: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF7C7C'];

export default function SalesDashboard() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const routerState = useRouterState();

    const [dashboardData, setDashboardData] = useState<SalesDashboardData | null>(null);
    const [monthlyComparison, setMonthlyComparison] = useState<MonthlyComparisonResponse | null>(null);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [selectedCostCenter, setSelectedCostCenter] = useState<string>('all');
    const [daysBack, setDaysBack] = useState<number>(30);
    const [selectedMonth, setSelectedMonth] = useState<string>(() => {
        // Default to current month in YYYY-MM format
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'dashboard' | 'monthly'>('monthly');

    const formatCurrency = (value: number, currency?: string) => {
        const currencyCode = currency || 'HUF';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatMonthName = (monthStr: string) => {
        // Convert YYYY-MM format to readable month name
        const date = new Date(monthStr + '-01');
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(data);
            }
        } catch (err) {
            console.error('Error fetching cost centers:', err);
        }
    };

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();

            // If we're in monthly view mode, use the selected month to set date range
            if (viewMode === 'monthly') {
                const [year, month] = selectedMonth.split('-');
                const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
                const endDate = new Date(parseInt(year), parseInt(month), 0); // Last day of month

                params.append('start_date', startDate.toISOString().split('T')[0]);
                params.append('end_date', endDate.toISOString().split('T')[0]);
            } else {
                // Use days_back for period view
                params.append('days_back', daysBack.toString());
            }

            if (selectedCostCenter !== 'all') {
                params.append('cost_center_id', selectedCostCenter);
            }

            const response = await apiFetch(`/api/sales-analytics/dashboard?${params}`);

            if (!response.ok) {
                throw new Error(`Failed to fetch sales dashboard data: ${response.statusText}`);
            }

            const data = await response.json();
            setDashboardData(data);
        } catch (err) {
            console.error('Error fetching sales dashboard data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load sales dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchMonthlyComparison = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();

            // Use the selected month for comparison
            const [year, month] = selectedMonth.split('-');
            // Create date string directly to avoid timezone issues with toISOString()
            const apiParam = `${year}-${month.padStart(2, '0')}-01`;

            params.append('current_month', apiParam);

            if (selectedCostCenter !== 'all') {
                params.append('cost_center_id', selectedCostCenter);
            }

            const response = await apiFetch(`/api/sales-analytics/monthly-comparison?${params}`);

            if (!response.ok) {
                throw new Error(`Failed to fetch monthly comparison data: ${response.statusText}`);
            }

            const data = await response.json();
            setMonthlyComparison(data);
        } catch (err) {
            console.error('Error fetching monthly comparison data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load monthly comparison data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCostCenters();
    }, []);

    useEffect(() => {
        if (viewMode === 'dashboard') {
            fetchDashboardData();
        } else {
            fetchMonthlyComparison();
        }
    }, [selectedCostCenter, daysBack, selectedMonth, viewMode]);

    // Prepare chart data for daily revenue
    const dailyRevenueChartData = dashboardData?.daily_revenue.map(day => ({
        name: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: day.total_revenue,
        cafeRevenue: day.cafe_revenue || 0,
        b2bRevenue: day.b2b_revenue || 0,
        orders: day.order_count
    })) || [];

    // Prepare chart data for cost center revenue
    const costCenterChartData = dashboardData?.cost_center_revenue.map(cc => ({
        name: cc.cost_center_name,
        revenue: cc.total_revenue,
        orders: cc.order_count
    })) || [];

    // Prepare chart data for category revenue (top 8)
    const categoryChartData = dashboardData?.category_revenue
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 8)
        .map((cat, index) => ({
            name: cat.category_name,
            revenue: cat.total_revenue,
            fill: COLORS[index % COLORS.length]
        })) || [];

    // Prepare monthly comparison overlay data - always show current month + last 4 days of previous month
    const monthlyOverlayData = monthlyComparison ? (() => {
        const currentMonthData = monthlyComparison.current_month_daily;
        const previousMonthData = monthlyComparison.previous_month_daily;

        // Parse the selected month to determine the date range
        const [year, month] = selectedMonth.split('-');
        const currentYear = parseInt(year, 10);
        const currentMonth0 = parseInt(month, 10) - 1; // JS Date months are 0-based

        // Define the fixed date range: last 4 days of previous month + all of current month
        const currentMonthStart = new Date(currentYear, currentMonth0, 1);
        const currentMonthEnd = new Date(currentYear, currentMonth0 + 1, 0); // Last day of current month

        // Calculate the last 4 days of previous month
        const previousMonthEnd = new Date(currentYear, currentMonth0, 0); // Last day of previous month
        const chartStartDate = new Date(currentYear, currentMonth0 - 1, previousMonthEnd.getDate() - 3); // 4 days before end

        // Calculate corresponding dates in the comparison previous month (one month earlier)
        const comparisonYear = currentMonth0 === 0 ? currentYear - 1 : currentYear;
        const comparisonMonth0 = currentMonth0 === 0 ? 11 : currentMonth0 - 1;
        const comparisonMonthStart = new Date(comparisonYear, comparisonMonth0, 1);
        const comparisonMonthEnd = new Date(comparisonYear, comparisonMonth0 + 1, 0);
        const comparisonPreviousMonthEnd = new Date(comparisonYear, comparisonMonth0, 0);
        const comparisonChartStartDate = new Date(comparisonYear, comparisonMonth0 - 1, comparisonPreviousMonthEnd.getDate() - 3);

        // Generate data for each day in the range
        const data: any[] = [];
        const totalDays = Math.ceil((currentMonthEnd.getTime() - chartStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

        for (let i = 0; i < totalDays; i++) {
            const currentDate = new Date(chartStartDate);
            currentDate.setDate(chartStartDate.getDate() + i);

            const comparisonDate = new Date(comparisonChartStartDate);
            comparisonDate.setDate(comparisonChartStartDate.getDate() + i);

            // Find revenue data for these dates
            const currentRevenue = currentMonthData.find(d => {
                const dataDate = new Date(d.date);
                return dataDate.toDateString() === currentDate.toDateString();
            })?.total_revenue || 0;

            const previousRevenue = previousMonthData.find(d => {
                const dataDate = new Date(d.date);
                return dataDate.toDateString() === comparisonDate.toDateString();
            })?.total_revenue || 0;

            // Determine if dates are in the core month (not the 4-day prefix)
            const isCurrentInCoreMonth = currentDate >= currentMonthStart && currentDate <= currentMonthEnd;
            const isComparisonInCoreMonth = comparisonDate >= comparisonMonthStart && comparisonDate <= comparisonMonthEnd;

            const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
            const dayOfMonth = currentDate.getDate();

            data.push({
                day: `${dayOfWeek} ${dayOfMonth}`,
                dayOfWeek,
                dayOfMonth,
                current: currentRevenue,
                previous: previousRevenue,
                currentDate: currentDate.toISOString().split('T')[0],
                previousDate: comparisonDate.toISOString().split('T')[0],
                isCurrentCore: isCurrentInCoreMonth,
                isPreviousCore: isComparisonInCoreMonth
            });
        }

        return data;
    })() : [];


    // Adjust monthly comparison metrics to be period-to-date when the selected month is partial,
    // or when it is the current month. This avoids full-month vs partial-month bias.
    const adjustedComparison = React.useMemo<MonthlyComparisonData | null>(() => {
        if (!monthlyComparison) return null;

        const [selYearStr, selMonthStr] = selectedMonth.split('-');
        const selYear = parseInt(selYearStr, 10);
        const selMonth0 = parseInt(selMonthStr, 10) - 1; // JS Date months are 0-based

        const base = monthlyComparison.comparison_data;

        // Determine current month boundaries for the selected month
        const monthStart = new Date(selYear, selMonth0, 1);
        const monthEnd = new Date(selYear, selMonth0 + 1, 0);

        // Determine the last available day in the selected month's current data
        const currentDays = monthlyComparison.current_month_daily.map(d => new Date(`${d.date}T00:00:00`));
        if (currentDays.length === 0) return base;
        const currentMaxDay = currentDays.reduce((max, dt) => Math.max(max, dt.getDate()), 1);

        // If the selected month appears complete (max day equals month length), use base/full-month
        const daysInSelectedMonth = monthEnd.getDate();
        const isPartial = currentMaxDay < daysInSelectedMonth;

        // Also treat the selected current month as partial (period-to-date), even if data already looks complete
        const now = new Date();
        const isCurrentMonthSelected = now.getFullYear() === selYear && now.getMonth() === selMonth0;

        if (!isPartial && !isCurrentMonthSelected) return base;

        const cutoffDay = isCurrentMonthSelected ? now.getDate() : currentMaxDay;

        const sumUpTo = (
            list: DailyRevenueData[],
            dayLimit: number,
            rangeStart: Date,
            rangeEnd: Date
        ) => {
            let revenue = 0;
            let orders = 0;
            for (const d of list) {
                const dt = new Date(`${d.date}T00:00:00`);
                const day = dt.getDate();
                if (dt >= rangeStart && dt <= rangeEnd && day <= dayLimit) {
                    revenue += d.total_revenue || 0;
                    orders += d.order_count || 0;
                }
            }
            return { revenue, orders };
        };

        // Previous month boundaries
        const prevStart = new Date(selYear, selMonth0 - 1, 1);
        const prevEnd = new Date(selYear, selMonth0, 0);

        // Previous month may have fewer days; cap to its last day
        const prevMonthDates = monthlyComparison.previous_month_daily.map(d => new Date(`${d.date}T00:00:00`));
        const prevMonthLastDay = prevMonthDates.length
            ? prevMonthDates.reduce((max, dt) => Math.max(max, dt.getDate()), 0)
            : cutoffDay;
        const prevCutoff = Math.min(cutoffDay, prevMonthLastDay);

        const curSum = sumUpTo(monthlyComparison.current_month_daily, cutoffDay, monthStart, monthEnd);
        const prevSum = sumUpTo(monthlyComparison.previous_month_daily, prevCutoff, prevStart, prevEnd);

        const current_avg_order = curSum.orders > 0 ? curSum.revenue / curSum.orders : 0;
        const previous_avg_order = prevSum.orders > 0 ? prevSum.revenue / prevSum.orders : 0;

        const pct = (cur: number, prev: number) => {
            if (prev === 0) return cur > 0 ? 100 : 0;
            return ((cur - prev) / prev) * 100;
        };

        return {
            ...base,
            current_revenue: curSum.revenue,
            previous_revenue: prevSum.revenue,
            current_orders: curSum.orders,
            previous_orders: prevSum.orders,
            current_avg_order,
            previous_avg_order,
            revenue_growth: pct(curSum.revenue, prevSum.revenue),
            order_growth: pct(curSum.orders, prevSum.orders),
            avg_order_growth: pct(current_avg_order, previous_avg_order),
        };
    }, [monthlyComparison, selectedMonth]);

    // Human-friendly note to show which period is being compared when P2D is active
    const comparisonNote = React.useMemo<string | null>(() => {
        if (!monthlyComparison) return null;

        const [selYearStr, selMonthStr] = selectedMonth.split('-');
        const selYear = parseInt(selYearStr, 10);
        const selMonth0 = parseInt(selMonthStr, 10) - 1;

        const currentDays = monthlyComparison.current_month_daily.map(d => new Date(`${d.date}T00:00:00`));
        if (!currentDays.length) return null;

        const maxDay = currentDays.reduce((max, dt) => Math.max(max, dt.getDate()), 1);
        const daysInSelectedMonth = new Date(selYear, selMonth0 + 1, 0).getDate();
        const now = new Date();
        const isCurrentMonthSelected = now.getFullYear() === selYear && now.getMonth() === selMonth0;
        const isPartial = maxDay < daysInSelectedMonth || isCurrentMonthSelected;
        const cutoff = isCurrentMonthSelected ? now.getDate() : maxDay;

        if (!isPartial) return null;

        const monthName = new Date(selYear, selMonth0, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const prevMonthDate = new Date(selYear, selMonth0 - 1, 1);
        const prevMonthName = prevMonthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

        return `Comparing ${monthName} 1–${cutoff} vs ${prevMonthName} 1–${cutoff}`;
    }, [monthlyComparison, selectedMonth]);


    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg">{t('salesDashboard.loading', 'finance')}</div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-red-600">{t('status.error', 'common')}: {error}</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('salesDashboard.title', 'finance')}</h1>
                        <p className="text-gray-600">
                            {viewMode === 'monthly'
                                ? t('salesDashboard.subtitleMonthly', 'finance', { month: new Date(selectedMonth + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) })
                                : t('salesDashboard.subtitlePeriod', 'finance', { days: daysBack })
                            }
                        </p>
                    </div>

                    <div className="flex gap-4">
                        {/* View Mode Toggle */}
                        <div className="flex bg-gray-100 rounded-lg p-1">
                            <Button
                                variant={viewMode === 'monthly' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('monthly')}
                            >
                                {t('salesDashboard.monthlyView', 'finance')}
                            </Button>
                            <Button
                                variant={viewMode === 'dashboard' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setViewMode('dashboard')}
                            >
                                {t('salesDashboard.periodView', 'finance')}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex gap-4 items-center">
                    <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder={t('salesOrders.allLocations', 'finance')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('salesOrders.allLocations', 'finance')}</SelectItem>
                            {costCenters.map((cc) => (
                                <SelectItem key={cc.id} value={cc.id}>
                                    {cc.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {viewMode === 'dashboard' && (
                        <Select value={daysBack.toString()} onValueChange={(value) => setDaysBack(parseInt(value))}>
                            <SelectTrigger className="w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">7 {t('salesDashboard.days', 'finance')}</SelectItem>
                                <SelectItem value="14">14 {t('salesDashboard.days', 'finance')}</SelectItem>
                                <SelectItem value="30">30 {t('salesDashboard.days', 'finance')}</SelectItem>
                                <SelectItem value="60">60 {t('salesDashboard.days', 'finance')}</SelectItem>
                                <SelectItem value="90">90 {t('salesDashboard.days', 'finance')}</SelectItem>
                            </SelectContent>
                        </Select>
                    )}

                    {viewMode === 'monthly' && (
                        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                            <SelectTrigger className="w-40">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(() => {
                                    const months: any[] = [];
                                    const now = new Date();
                                    // Generate last 12 months
                                    for (let i = 0; i < 12; i++) {
                                        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                                        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                        const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                                        months.push(
                                            <SelectItem key={value} value={value}>
                                                {label}
                                            </SelectItem>
                                        );
                                    }
                                    return months;
                                })()}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                {/* Monthly Comparison View */}
                {viewMode === 'monthly' && monthlyComparison && (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card>
                                <CardHeader className="border-b-dashed">
                                    <CardTitle className="">{t('salesDashboard.revenueGrowth', 'finance')}</CardTitle>
                                    {(adjustedComparison || monthlyComparison.comparison_data).revenue_growth >= 0 ? (
                                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />
                                    )}
                                </CardHeader>
                                <CardContent className="p-4">
                                    <div className="text-2xl font-bold">
                                        {(adjustedComparison || monthlyComparison.comparison_data).revenue_growth >= 0 ? '+' : ''}
                                        {(adjustedComparison || monthlyComparison.comparison_data).revenue_growth.toFixed(1)}%
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {formatCurrency((adjustedComparison || monthlyComparison.comparison_data).current_revenue, monthlyComparison.base_currency)} {t('salesDashboard.vs', 'finance')} {formatCurrency((adjustedComparison || monthlyComparison.comparison_data).previous_revenue, monthlyComparison.base_currency)}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="border-b-dashed">
                                    <CardTitle className="">{t('salesDashboard.orderGrowth', 'finance')}</CardTitle>
                                    {(adjustedComparison || monthlyComparison.comparison_data).order_growth >= 0 ? (
                                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />
                                    )}
                                </CardHeader>
                                <CardContent className="p-4">
                                    <div className="text-2xl font-bold">
                                        {(adjustedComparison || monthlyComparison.comparison_data).order_growth >= 0 ? '+' : ''}
                                        {(adjustedComparison || monthlyComparison.comparison_data).order_growth.toFixed(1)}%
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {(adjustedComparison || monthlyComparison.comparison_data).current_orders} {t('salesDashboard.vs', 'finance')} {(adjustedComparison || monthlyComparison.comparison_data).previous_orders} {t('salesDashboard.ordersLabel', 'finance')}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="border-b-dashed">
                                    <CardTitle className="">{t('salesDashboard.avgOrderValueGrowth', 'finance')}</CardTitle>
                                    {(adjustedComparison || monthlyComparison.comparison_data).avg_order_growth >= 0 ? (
                                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <ArrowTrendingDownIcon className="h-4 w-4 text-red-600" />
                                    )}
                                </CardHeader>
                                <CardContent className="p-4">
                                    <div className="text-2xl font-bold">
                                        {(adjustedComparison || monthlyComparison.comparison_data).avg_order_growth >= 0 ? '+' : ''}
                                        {(adjustedComparison || monthlyComparison.comparison_data).avg_order_growth.toFixed(1)}%
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {formatCurrency((adjustedComparison || monthlyComparison.comparison_data).current_avg_order, monthlyComparison.base_currency)} {t('salesDashboard.vs', 'finance')} {formatCurrency((adjustedComparison || monthlyComparison.comparison_data).previous_avg_order, monthlyComparison.base_currency)}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {comparisonNote && (
                            <p className="text-xs text-gray-500 italic -mt-2 mb-2">{comparisonNote}</p>
                        )}

                        {/* Monthly Overlay Chart */}
                        <Card>
                            <CardHeader className="border-b">
                                <CardTitle>{t('salesDashboard.dailyRevenueComparisonTitle', 'finance')}</CardTitle>
                                <CardDescription>
                                    {t('salesDashboard.dailyRevenueComparisonDesc', 'finance', { current: formatMonthName(monthlyComparison.comparison_data.current_month), previous: formatMonthName(monthlyComparison.comparison_data.previous_month) })}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="p-4">
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={monthlyOverlayData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="day"
                                                tick={{ fontSize: 12 }}
                                                angle={-45}
                                                textAnchor="end"
                                                height={80}
                                            />
                                            <YAxis tickFormatter={(value) => formatCurrency(value, monthlyComparison.base_currency)} tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [
                                                    formatCurrency(Number(value), monthlyComparison.base_currency),
                                                    name
                                                ]}
                                                labelFormatter={(label, payload) => {
                                                    if (payload && payload.length > 0) {
                                                        const data = payload[0].payload;
                                                        const currentNote = data.isCurrentCore ? '' : ` ${t('salesDashboard.extended', 'finance')}`;
                                                        const previousNote = data.isPreviousCore ? '' : ` ${t('salesDashboard.extended', 'finance')}`;
                                                        return `${label}\n${t('salesDashboard.current', 'finance')}: ${data.currentDate}${currentNote}\n${t('salesDashboard.previous', 'finance')}: ${data.previousDate}${previousNote}`;
                                                    }
                                                    return label;
                                                }}
                                            />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="current"
                                                stroke="#0088FE"
                                                strokeWidth={2}
                                                name={`${t('salesDashboard.current', 'finance')} (${formatMonthName(monthlyComparison.comparison_data.current_month)})`}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="previous"
                                                stroke="#82CA9D"
                                                strokeWidth={2}
                                                strokeDasharray="5 5"
                                                name={`${t('salesDashboard.previous', 'finance')} (${formatMonthName(monthlyComparison.comparison_data.previous_month)})`}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </>
                )}

                {/* Period Dashboard View */}
                {viewMode === 'dashboard' && dashboardData && (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">{t('salesDashboard.totalRevenue', 'finance')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {formatCurrency(dashboardData.total_revenue, dashboardData.base_currency)}
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {t('salesDashboard.lastXDays', 'finance', { days: daysBack })}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">{t('salesDashboard.totalOrders', 'finance')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{dashboardData.total_orders.toLocaleString()}</div>
                                    <p className="text-xs text-gray-600">
                                        {t('salesDashboard.lastXDays', 'finance', { days: daysBack })}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">{t('salesDashboard.avgOrderValue', 'finance')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {formatCurrency(dashboardData.avg_order_value, dashboardData.base_currency)}
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {t('salesDashboard.perOrder', 'finance')}
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">{t('salesDashboard.avgDailyRevenue', 'finance')}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">
                                        {formatCurrency(dashboardData.avg_daily_revenue, dashboardData.base_currency)}
                                    </div>
                                    <p className="text-xs text-gray-600">
                                        {t('salesDashboard.perDay', 'finance')}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Charts Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Daily Revenue Chart */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('salesDashboard.dailyRevenue', 'finance')}</CardTitle>
                                    <CardDescription>
                                        {t('salesDashboard.dailyRevenueDesc', 'finance', { days: daysBack })}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={dailyRevenueChartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={(value) => formatCurrency(value, dashboardData.base_currency)} tick={{ fontSize: 12 }} />
                                                <Tooltip
                                                    formatter={(value, name) => {
                                                        const label = name === 'cafeRevenue'
                                                            ? t('salesDashboard.cafeRevenue', 'finance') || 'Cafe Revenue'
                                                            : name === 'b2bRevenue'
                                                                ? t('salesDashboard.b2bRevenue', 'finance') || 'B2B Revenue'
                                                                : t('salesDashboard.revenueLabel', 'finance');
                                                        return [formatCurrency(Number(value), dashboardData.base_currency), label];
                                                    }}
                                                />
                                                <Legend
                                                    formatter={(value) => {
                                                        if (value === 'cafeRevenue') return t('salesDashboard.cafeRevenue', 'finance') || 'Cafe Revenue';
                                                        if (value === 'b2bRevenue') return t('salesDashboard.b2bRevenue', 'finance') || 'B2B Revenue';
                                                        return value;
                                                    }}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="cafeRevenue"
                                                    stackId="1"
                                                    stroke="#8884d8"
                                                    fill="#8884d8"
                                                    name="cafeRevenue"
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="b2bRevenue"
                                                    stackId="1"
                                                    stroke="#82ca9d"
                                                    fill="#82ca9d"
                                                    name="b2bRevenue"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Revenue by Cafe Chart */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('salesDashboard.revenueByCafe', 'finance')}</CardTitle>
                                    <CardDescription>
                                        {t('salesDashboard.revenueByCafeDesc', 'finance')}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={costCenterChartData} >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={(value) => formatCurrency(value, dashboardData.base_currency)} tick={{ fontSize: 12 }} />
                                                <Tooltip formatter={(value) => [formatCurrency(Number(value), dashboardData.base_currency), t('salesDashboard.revenueLabel', 'finance')]} />
                                                <Legend />
                                                <Bar dataKey="revenue" fill="#00C49F" name={t('salesDashboard.revenueLabel', 'finance')} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Revenue by Category Chart */}
                        <Card>
                            <CardHeader>
                                <CardTitle>{t('salesDashboard.revenueByCategory', 'finance')}</CardTitle>
                                <CardDescription>
                                    {t('salesDashboard.revenueByCategoryDesc', 'finance')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={categoryChartData}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, value }) => {
                                                    const total = categoryChartData.reduce((sum, item) => sum + item.revenue, 0);
                                                    const percent = total > 0 ? ((Number(value) || 0) / total * 100).toFixed(0) : 0;
                                                    return `${name} ${percent}%`;
                                                }}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="revenue"
                                            >
                                                {categoryChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => [formatCurrency(Number(value), dashboardData.base_currency), t('salesDashboard.revenueLabel', 'finance')]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/sales/dashboard')({
    component: SalesDashboard,
})
